// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import { func, array, string, object, bool } from 'prop-types';
import styled from '@emotion/styled';
import { Button, Dropdown, Loading, RadioButtonGroup, RadioButton } from '@carbon/react';
import { Filter, ArrowDownWideNarrow, X, Grid3x3, List, Sheet } from 'lucide-react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import XlsIcon from 'Assets/images/xls.svg';
import CsvIcon from 'Assets/images/csv.svg';

const StyledToolbar = styled.div`
  .toolbar {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    width: 100%;
    border-top: 2px solid ${ props => props.theme.products.colors.border };
    padding-top: 10px;
    line-height: 1rem;

    > div {
      display: flex;
      align-items: center;

      &:first-of-type {
        flex-grow: 1;
      }
    }

    .buttons {
      display: flex;
      align-items: center;
      justify-content: space-between;

      button {
        height: 35px;
        width: 35px;
        justify-content: center;
        min-height: auto;
        min-width: auto;

        svg {
          width: auto;
          height: auto;
          transform: scale(0.9);
        }
      }

      .display {
        display: flex;
        align-items: center;

        p {
          font-size: 0.875rem;
          line-height: 1rem;
          margin-right: 7px;
          color: ${ props => props.theme.colors.darkGray };
        }

        button {
          &:active,
          &:focus,
          &:hover,
          &.active {
            background-color: transparent;
            border-color: ${ props => props.theme.products.colors.border } !important;
            outline: 0 none;
            box-shadow: none;
            color: ${ props => props.theme.colors.darkGray };
          }

          &:nth-of-type(2n) {
            border-left: 0;
            border-right: 0;
          }

          svg {
            margin: 0 !important;
          }

          color: ${ props => props.theme.colors.lightGray };
          border: 1px solid ${ props => props.theme.products.colors.border };

        }
      }

      .download {
        margin: 0 -5px 0 1.25rem;
        display: flex;

        button {
          &:disabled {
            .fileType {
              opacity: 0.6;
            }
          }

          .cds--loading {
            position: absolute;
            left: auto;
            top: 29%;
            width: 15px;
            height: 15px;
            z-index: 1;
          }

          display: flex;
          align-items: center;
          justify-content: center;
          background-color: transparent !important;
          color: transparent;
          box-shadow: none !important;
          border: 0 !important;
          width: 26px;
          margin: 0 5px;
          padding: 0;

          .fileType {
            transform: none;
            min-width: 27px;
            min-height: 31px;
          }
        }
      }
    } 
  }

  .mobileToolbar {
    display: none;
    gap: 1em;
    margin-bottom: 18px;

    &.fixed {
      display: none;
      justify-content: center;
      align-items: center;
      padding: 15px;
      margin: 0;
      width: 100vw;
      height: 80px;
      position: fixed;
      bottom: 0;
      left: 0;
      transition: transform 0.2s ease;
      overflow: visible;
      opacity: ${ props => props.isVisible ? 1 : 0 };
      visibility: ${ props => props.isVisible ? 'visible' : 'hidden' };
      background-color: ${ props => props.theme.colors.white };
      box-shadow: 0 3px 6px #00000029;
      z-index: 8999;
    }

    button {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 8px;
      background: #E7ECF2;
      border: 1px solid #B2C2D5;
      border-radius: 10px;
      cursor: pointer;

      .title {
        display: flex;
        align-items: center;
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;

        svg {
          margin-right: 8px;
        }
      }

      .info {
        color: #707070;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }

  .backDrop {
    display: none;
  }

  @media screen and (max-width: 767px) {
    .toolbar {
      display: none;
    }

    .mobileToolbar {
      display: flex;

      &.fixed {
        display: flex;
      }
    }

    .backDrop {
      position: fixed;
      inset: 0;
      z-index: 10;
      background: #000000b3;
      display: ${ props => props.mobileSortOpen ? 'flex' : 'none' };
      align-items: flex-end;

      .content {
        flex: 1;
        padding: 15px;
        background: #fff;

        .header {
          display: flex;
          justify-content: space-between;
          margin-bottom: 10px;

          h3 {
            font-size: 20px;
            line-height: 27px;
          }

          button {
            background: transparent;
            border: none;
            cursor: pointer;
          }
        }

        .cds--radio-button-group {
          display: flex;
          flex-direction: column;
          align-items: flex-start;

          .cds--radio-button-wrapper {
            margin: 12px 0;
          }
        }
      }
    }
  }
`;

const StyledDropdown = styled.div`
  &:last-of-type {
    margin-left: 10px;
  }

  .cds--dropdown__wrapper {
    align-items: center;
  }

  .cds--dropdown {
    background-color: transparent;
    position: relative;
    border: 1px solid ${ props => props.theme.products.colors.border };
    max-height: 35px;
  }

  .cds--list-box__menu-item__option {
    padding: 0;
    a {
      display: block;
      padding: 0.6875rem 1.5rem 0.6875rem 0;
      width: 100%;
      color: ${ props => props.theme.colors.darkGray };
    }
  }

  .cds--list-box__menu {
    min-width: 190px;
  }

  label {
    font-size: 0.875rem;
    line-height: 1rem;
    font-weight: 600;
    color: ${ props => props.theme.colors.darkGray };
  }
`;

const ToolbarHeaderComponent = ({
  sortOptions,
  perPageOptions,
  currentPerPage,
  viewMode,
  onDownload,
  itemToElement,
  currentSort,
  downloadingType,
  onViewChange,
  setMobileFiltersOpen,
  activeFilters,
  handleMobileOrderChange,
  mobileSortOpen,
  setMobileSortOpen,
  recordsPerPage,
}) => {
  const [mobileInView, setMobileInView] = useState(false);

  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <StyledToolbar mobileSortOpen={ mobileSortOpen } isVisible={ !mobileInView }>
      { recordsPerPage > 0 && (
        <div className="toolbar">
          <div>
            <StyledDropdown theme={ theme }>
              <Dropdown
                id="sort"
                type="inline"
                titleText={ t('products.sortBy') }
                label={ t('products.sortOptions') }
                items={ sortOptions }
                itemToElement={ itemToElement('order') }
                selectedItem={ currentSort }
              />
            </StyledDropdown>
            <StyledDropdown theme={ theme } className="items">
              <Dropdown
                id="per_page"
                type="inline"
                titleText={ t('products.perPage') }
                label={ t('products.elements') }
                items={ perPageOptions }
                itemToElement={ itemToElement('count') }
                initialSelectedItem={ currentPerPage }
              />
            </StyledDropdown>
          </div>
          <div className="buttons">
            <div className="display">
              <p>{ t('products.displayMode') }: </p>
              <Button
                kind="tertiary"
                className={ viewMode === 'list' ? 'active' : '' }
                renderIcon={ List }
                hasIconOnly
                data-test="onViewMode"
                data-testid="list"
                iconDescription={ t('products.displayModeList') }
                onClick={ () => onViewChange('list') }
              />
              <Button
                kind="tertiary"
                className={ viewMode === 'grid' ? 'active' : '' }
                renderIcon={ Grid3x3 }
                hasIconOnly
                data-test="onViewMode"
                data-testid="grid"
                iconDescription={ t('products.displayModeGrid') }
                onClick={ () => onViewChange('grid') }
              />
              <Button
                kind="tertiary"
                className={ viewMode === 'table' ? 'active' : '' }
                renderIcon={ Sheet }
                hasIconOnly
                data-test="onViewMode"
                data-testid="table"
                iconDescription={ t('products.displayModeTable') }
                onClick={ () => onViewChange('table') }
              />
            </div>
            <div className="download">
              <Button
                kind="ghost"
                className="csv"
                hasIconOnly
                disabled={ downloadingType === 'csv' }
                iconDescription={ t('products.downloadCsv') }
                onClick={ () => onDownload('csv') }
              >
                { (downloadingType === 'csv') && <Loading active small withOverlay={ false } /> }
                <CsvIcon className="fileType" />
              </Button>
              <Button
                kind="ghost"
                className="xls"
                hasIconOnly
                iconDescription={ t('products.downloadXls') }
                disabled={ downloadingType === 'xlsx' }
                onClick={ () => onDownload('xlsx') }
              >
                { (downloadingType === 'xlsx') && <Loading active small withOverlay={ false } /> }
                <XlsIcon className="fileType" />
              </Button>

            </div>
          </div>
        </div>
      )}
      <InView className="mobileToolbar" onChange={ setMobileInView }>
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ activeFilters } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </InView>
      <div className="mobileToolbar fixed">
        <button type="button" onClick={ () => setMobileFiltersOpen(true) }>
          <span className="title">
            <Filter />
            { t('filters.filter') }
          </span>
          <span className="info">{ `${ activeFilters } ${t('filters.selectedFilters')}`} </span>
        </button>
        <button type="button" onClick={ () => setMobileSortOpen(true) }>
          <span className="title">
            <ArrowDownWideNarrow />
            { t('products.sort') }
          </span>
          <span className="info">{ currentSort?.label }</span>
        </button>
      </div>
      <div className="mobileSort">
        <div className="backDrop">
          <div className="content">
            <div className="header">
              <h3>{ t('products.sortBy') }</h3>
              <button type="button" onClick={ () => setMobileSortOpen(false) }>
                <X />
              </button>
            </div>
            <RadioButtonGroup
              name="sortProducts"
              valueSelected={ currentSort?.id }
              onChange={ handleMobileOrderChange }
            >
              { sortOptions.map(opt => (
                <RadioButton labelText={ opt.label } value={ opt.id } id={ opt.id } />
              )) }
            </RadioButtonGroup>
          </div>
        </div>
      </div>
    </StyledToolbar>
  );
};

ToolbarHeaderComponent.propTypes = {
  downloadingType: string,
  onDownload: func,
  onViewChange: func,
  viewMode: string,
  sortOptions: array,
  itemToElement: func,
  currentSort: object,
  perPageOptions: array,
  currentPerPage: object,
  setMobileFiltersOpen: func,
  activeFilters: object,
  handleMobileOrderChange: func,
  mobileSortOpen: bool,
  setMobileSortOpen: func,
  recordsPerPage: string,
};

export default ToolbarHeaderComponent;
